import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Navigation } from '../components/Navigation';
import { Footer } from '../components/Footer';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ExternalLink, Calendar, Music } from 'lucide-react';
import { PageLayout } from '../components/layouts/PageLayout';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ReleaseDetailPage = () => {
  const { releaseId } = useParams();
  const [release, setRelease] = useState(null);
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleElements, setVisibleElements] = useState([]);

  const fetchArtists = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/artists`);
      setArtists(response.data);
    } catch (error) {
      console.error('Failed to fetch artists:', error);
    }
  }, []);

  const fetchReleaseData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/releases/${releaseId}`);
      setRelease(response.data);
    } catch (error) {
      console.error('Failed to fetch release:', error);
    } finally {
      setLoading(false);
    }
  }, [releaseId]);

  useEffect(() => {
    fetchReleaseData();
    fetchArtists();
  }, [fetchReleaseData, fetchArtists]);

  useEffect(() => {
    // Intersection Observer for scroll-based animations
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisibleElements((prev) => [...new Set([...prev, entry.target.dataset.index])]);
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -100px 0px' }
    );

    const elements = document.querySelectorAll('[data-animate]');
    elements.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, [release]);

  // Find artist by name
  const getArtistByName = (artistName) => {
    return artists.find(artist => artist.name.toLowerCase() === artistName.toLowerCase());
  };

  // Extract iframe src from Bandcamp embed code
  const extractBandcampSrc = (embedCode) => {
    if (!embedCode) return null;
    const match = embedCode.match(/src="([^"]+)"/);
    return match ? match[1] : null;
  };

  if (loading) {
    return (
      <PageLayout primaryColor="green" secondaryColor="orange">
        <div className="page-transition min-h-screen">
          <Navigation />
          <div className="pt-32 container mx-auto px-4 text-center">
            <p className="text-white text-xl">Loading...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!release) {
    return (
      <PageLayout primaryColor="green" secondaryColor="orange">
        <div className="page-transition min-h-screen">
          <Navigation />
          <div className="pt-32 container mx-auto px-4 text-center">
            <p className="text-white text-xl">Release not found</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const embedSrc = extractBandcampSrc(release.bandcampEmbed);

  return (
    <PageLayout primaryColor="green" secondaryColor="orange">
      <div className="page-transition min-h-screen">
        <Navigation />
        
        {/* Release Header */}
        <section className="pt-32 pb-16">
          <div className="container mx-auto px-4">
            {/* Title and Type */}
            <div 
              className="text-center mb-12 transition-all duration-700 ease-out"
              data-animate
              data-index="header"
              style={{
                opacity: visibleElements.includes('header') ? 1 : 0,
                transform: visibleElements.includes('header') ? 'translateY(0)' : 'translateY(30px)'
              }}
            >
              <div className="flex justify-center mb-4">
                <span className="bg-gradient-to-r from-orange-500 to-green-500 text-white text-sm font-bold px-6 py-2 rounded-full hover:from-orange-600 hover:to-green-600 hover:scale-110 hover:shadow-lg hover:shadow-orange-500/50 transition-all duration-300 cursor-default">
                  {release.type}
                </span>
              </div>
              <h1 className="text-5xl md:text-7xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 hover:scale-105 transition-transform duration-300 cursor-default leading-tight pb-2">
                {release.title}
              </h1>
              {release.artists && release.artists[0] && getArtistByName(release.artists[0]) ? (
                <Link to={`/artists/${getArtistByName(release.artists[0]).id}`}>
                  <p className="text-2xl text-gray-300 mb-2 hover:text-orange-400 transition-colors duration-300 cursor-pointer inline-block">
                    by {release.artists.join(', ')}
                  </p>
                </Link>
              ) : (
                <p className="text-2xl text-gray-300 mb-2">by {release.artists?.join(', ') || 'Unknown Artist'}</p>
              )}
              <div className="flex items-center justify-center gap-6 text-gray-500 mb-6">
                {release.genre && (
                  <div className="flex items-center">
                    <Music className="w-5 h-5 mr-2" />
                    {release.genre}
                  </div>
                )}
                <div className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  {release.releaseDate}
                </div>
              </div>
            </div>

            <div className="flex flex-col lg:flex-row gap-8 items-start">
              {/* Album Covers */}
              <div 
                className="w-full lg:w-2/5"
                data-animate
                data-index="covers"
                style={{
                  opacity: visibleElements.includes('covers') ? 1 : 0,
                  transform: visibleElements.includes('covers') ? 'translateX(0)' : 'translateX(-30px)',
                  transition: 'all 0.7s ease-out 0.2s'
                }}
              >
                <div className="space-y-6">
                  {/* Front Cover */}
                  <div className="relative aspect-square overflow-hidden rounded-2xl shadow-2xl shadow-orange-500/30 hover:shadow-orange-500/60 hover:scale-105 transition-all duration-500 group cursor-pointer">
                    <img 
                      src={release.artwork} 
                      alt={`${release.title} - Front Cover`}
                      referrerPolicy="no-referrer"
                      
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-orange-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  </div>
                  
                  {/* Back Cover */}
                  {release.backCover && (
                    <div className="relative aspect-square overflow-hidden rounded-2xl shadow-2xl shadow-green-500/30 hover:shadow-green-500/60 hover:scale-105 transition-all duration-500 group cursor-pointer">
                      <img 
                        src={release.backCover} 
                        alt={`${release.title} - Back Cover`}
                        referrerPolicy="no-referrer"
                        
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-green-500/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                    </div>
                  )}
                </div>
              </div>

              {/* Release Info and Player */}
              <div 
                className="w-full lg:w-3/5 space-y-8"
                data-animate
                data-index="info"
                style={{
                  opacity: visibleElements.includes('info') ? 1 : 0,
                  transform: visibleElements.includes('info') ? 'translateX(0)' : 'translateX(30px)',
                  transition: 'all 0.7s ease-out 0.3s'
                }}
              >
                {/* Description */}
                {release.description && (
                  <Card className="bg-black/50 border-orange-500/30 hover:border-orange-500/60 hover:shadow-xl hover:shadow-orange-500/30 p-6 transition-all duration-300">
                    <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-green-400 mb-4 hover:scale-105 transition-transform duration-300 inline-block cursor-default leading-tight pb-1">
                      About This Release
                    </h2>
                    <p className="text-gray-300 text-lg leading-relaxed whitespace-pre-wrap hover:text-gray-200 transition-colors duration-300">
                      {release.description}
                    </p>
                  </Card>
                )}

                {/* Music Player - Bandcamp Embed */}
                {embedSrc ? (
                  <Card className="bg-black/50 border-green-500/30 hover:border-green-500/60 hover:shadow-xl hover:shadow-green-500/30 p-6 transition-all duration-300">
                    <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-orange-400 mb-4 flex items-center hover:scale-105 transition-transform duration-300 cursor-default leading-tight pb-1">
                      <Music className="w-6 h-6 mr-2" />
                      Listen Now
                    </h2>
                    <div className="w-full" style={{ height: '470px' }}>
                      <iframe 
                        style={{ border: 0, width: '100%', height: '100%' }}
                        src={embedSrc}
                        seamless
                        title={release.title}
                      >
                        <a href={release.bandcampUrl}>
                          {release.title} by {release.artist}
                        </a>
                      </iframe>
                    </div>
                  </Card>
                ) : release.bandcampUrl ? (
                  <Card className="bg-black/50 border-green-500/30 hover:border-green-500/60 hover:shadow-xl hover:shadow-green-500/30 p-6 transition-all duration-300">
                    <h2 className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-orange-400 mb-4 hover:scale-105 transition-transform duration-300 inline-block cursor-default leading-tight pb-1">
                      Listen on Bandcamp
                    </h2>
                    <Button
                      onClick={() => window.open(release.bandcampUrl, '_blank')}
                      className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 hover:scale-105 hover:shadow-lg hover:shadow-green-500/50 text-white py-6 text-lg transition-all duration-300"
                    >
                      <ExternalLink className="w-5 h-5 mr-2" />
                      Open in Bandcamp
                    </Button>
                  </Card>
                ) : null}

                {/* External Links */}
                <div className="flex gap-4">
                  <Button
                    onClick={() => window.open(release.soundcloudUrl, '_blank')}
                    className="flex-1 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800 hover:scale-105 hover:shadow-lg hover:shadow-orange-500/50 text-white py-6 text-lg transition-all duration-300"
                  >
                    <ExternalLink className="w-5 h-5 mr-2" />
                    SoundCloud
                  </Button>
                  {release.bandcampUrl && (
                    <Button
                      onClick={() => window.open(release.bandcampUrl, '_blank')}
                      className="flex-1 bg-gradient-to-r from-cyan-600 to-cyan-700 hover:from-cyan-700 hover:to-cyan-800 hover:scale-105 hover:shadow-lg hover:shadow-cyan-500/50 text-white py-6 text-lg transition-all duration-300"
                    >
                      <Music className="w-5 h-5 mr-2" />
                      Bandcamp
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </PageLayout>
  );
};
